/**
 * Native Home Screen quick actions (long-press the app icon).
 *
 * iOS: `UIApplicationShortcutItems` (Info.plist) + AppDelegate's `performActionFor shortcutItem` reuse the
 * standard `open url` bridge, so a shortcut tap arrives as a normal `appUrlOpen` on
 * `com.joinlighthouse.app://action/<type>` (see lighthouse-mobile's AppDelegate.swift). Android:
 * `res/xml/shortcuts.xml` intents point at the same URLs through MainActivity's intent-filter. Both reach us
 * through the one dispatcher in `src/native/appUrl.ts` (cold starts included).
 *
 * | Type | Does |
 * |---|---|
 * | `action/ask` | Home with the phone chat open and the dock focused (`?ask=1`, Home owns the opening) |
 * | `action/call` | Starts a voice call (`voiceCall.start`, a new conversation); where calls can't work (no voice host, an old binary without the speech plugin) it opens the chat like `action/ask` |
 * | `action/new-task` | Home's new-task form (`?newTask=1`) |
 * | `action/compose` | Inbox compose (`?compose=1`) |
 *
 * A shortcut often arrives before the session is restored (cold start). It is queued, not dropped, and runs once
 * a signed-in, onboarded user is loaded; it expires after a couple of minutes so it never fires out of the blue.
 *
 * Calls normally start inside a tap so audio can unlock. A quick action has no tap in the page, but the native
 * shell lets media play without one (iOS `mediaTypesRequiringUserActionForPlayback = []`, Android
 * `setMediaPlaybackRequiresUserGesture(false)`), so `voiceCall.start()` unlocks audio here too; if a WebView ever
 * refused, the call screen's first tap (orb / retry) unlocks it.
 */
import { watch } from 'vue'
import type { Router } from 'vue-router'
import useUser from '@/composables/useUser'
import { isOnboardingShellLocked } from '@/onboarding/shellLock'
import { onAppUrlOpen } from '@/native/appUrl'
import { voiceCall } from '@/voice/useVoiceCall'
import { isNativePlatform } from './platform'

export type ShortcutTarget =
    | { kind: 'route'; section: string; query: Record<string, string> }
    | { kind: 'call' }

export const SHORTCUT_ROUTES: Record<string, ShortcutTarget> = {
    'action/ask': { kind: 'route', section: 'home', query: { ask: '1' } },
    'action/call': { kind: 'call' },
    'action/new-task': { kind: 'route', section: 'home', query: { newTask: '1' } },
    'action/compose': { kind: 'route', section: 'inbox', query: { compose: '1' } },
}

/** A queued shortcut runs only within this long of its tap. */
export const SHORTCUT_MAX_AGE_MS = 2 * 60 * 1000

const SHORTCUT_URL_RE = /^[a-z][a-z0-9+.-]*:\/\/(action\/[a-z0-9-]+)\/?(?:[?#].*)?$/i

/** The quick action a URL names, or null (OAuth callbacks, deep links and unknown actions). */
export function shortcutForUrl(url: string): ShortcutTarget | null {
    const m = SHORTCUT_URL_RE.exec((url || '').trim())
    if (!m) return null
    return SHORTCUT_ROUTES[m[1].toLowerCase()] ?? null
}

export interface ShortcutQueue {
    /** A shortcut arrived. Runs now when ready, otherwise waits (the latest tap wins). */
    push(target: ShortcutTarget, now?: number): void
    /** Readiness may have changed: run the waiting shortcut if it is still fresh. */
    flush(now?: number): void
    /** Forget the waiting shortcut (e.g. the account can't use it). */
    drop(): void
    readonly pending: ShortcutTarget | null
}

export function createShortcutQueue(opts: {
    isReady: () => boolean
    run: (target: ShortcutTarget) => void
    maxAgeMs?: number
}): ShortcutQueue {
    const maxAge = opts.maxAgeMs ?? SHORTCUT_MAX_AGE_MS
    let waiting: { target: ShortcutTarget; at: number } | null = null
    const flush = (now = Date.now()) => {
        if (!waiting) return
        if (now - waiting.at > maxAge) {
            waiting = null
            return
        }
        if (!opts.isReady()) return
        const { target } = waiting
        waiting = null
        opts.run(target)
    }
    return {
        push(target, now = Date.now()) {
            waiting = { target, at: now }
            flush(now)
        },
        flush,
        drop() {
            waiting = null
        },
        get pending() {
            return waiting?.target ?? null
        },
    }
}

/** Start a voice call from a quick action. Returns false where calls can't work here. */
export function startShortcutCall(): boolean {
    if (!voiceCall.available()) return false
    // A new conversation; the call's "Chat" button routes to it (the default).
    return voiceCall.start({ surface: 'home' })
}

let initialized = false

export function initNativeShortcuts(router: Router) {
    if (!isNativePlatform() || initialized) return
    initialized = true
    const { user } = useUser()

    const go = (target: ShortcutTarget) => {
        if (target.kind === 'call') {
            if (startShortcutCall()) return
            // No call here (no mic path in this binary): the chat, with its call button, is the closest thing.
            go(SHORTCUT_ROUTES['action/ask'])
            return
        }
        void router.push({ name: 'dashboard', params: { section: target.section }, query: target.query })
    }

    const queue = createShortcutQueue({
        isReady: () => !!user.value?.id && !isOnboardingShellLocked(user.value),
        run: (target) => {
            void router.isReady().then(() => go(target))
        },
    })

    onAppUrlOpen((url) => {
        const target = shortcutForUrl(url)
        if (!target) return false
        queue.push(target)
        return true
    })

    watch(
        () => [user.value?.id, user.value?.onboarded] as const,
        () => {
            // The rail owns the shell until onboarding is done: a quick action has nowhere to land.
            if (user.value?.id && isOnboardingShellLocked(user.value)) queue.drop()
            else queue.flush()
        },
    )
}
